import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, X, Loader2, Copy, Download, KeyRound, Smartphone, Building2 } from "lucide-react";
import { API, formatApiError, setAuthToken } from "../auth/api";

/**
 * 2026-10-25: To-trinsbekræftelse (2FA) og adgangskode.
 *
 * Administratorer kan logge ind med en kode fra en godkendelses-app
 * (Microsoft Authenticator, Google Authenticator m.fl.) ud over kodeordet.
 * Firmaet bestemmer selv, om det er krævet (CompanyMfaRule): standard krævet
 * hos logistikfirmaer (løndata), valgfrit hos containerkunder. Chauffører og
 * bestillere bruger aldrig 2FA. MfaSetup bruges både ved første login (når
 * det er krævet) og her under "Login og sikkerhed". Gendannelseskoderne vises
 * kun én gang - de bruges, hvis telefonen mistes.
 */

const inputCls = "w-full px-3 py-2.5 border-2 border-slate-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-xl text-base focus:border-orange-500 outline-none";
const btn = "inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm disabled:opacity-50";

export function RecoveryCodes({ codes, onDone }) {
  const text = codes.join("\n");
  const copy = async () => { try { await navigator.clipboard.writeText(text); toast.success("Kopieret"); } catch (_) { toast.error("Kunne ikke kopiere - skriv dem ned"); } };
  const download = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([`ContainerFlow - gendannelseskoder\nHver kode kan bruges én gang, hvis du mister telefonen.\n\n${text}\n`], { type: "text/plain" }));
    a.download = "containerflow-gendannelseskoder.txt";
    a.click();
  };
  return (
    <div className="space-y-3" data-testid="mfa-recovery-codes">
      <p className="text-sm text-slate-600 dark:text-slate-300">Gem disse <b>gendannelseskoder</b> et sikkert sted (fx i din adgangskode-manager). Mister du telefonen, kan du logge ind med én af dem - hver kode virker én gang. De vises ikke igen.</p>
      <div className="grid grid-cols-2 gap-1.5 font-mono text-sm bg-slate-50 dark:bg-slate-900 rounded-xl p-3">
        {codes.map(c => <span key={c}>{c}</span>)}
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={copy} className={`${btn} bg-slate-100 dark:bg-slate-700`}><Copy className="w-4 h-4" /> Kopiér</button>
        <button type="button" onClick={download} className={`${btn} bg-slate-100 dark:bg-slate-700`}><Download className="w-4 h-4" /> Hent som fil</button>
        {onDone && <button type="button" onClick={onDone} className={`${btn} bg-orange-500 text-white flex-1`} data-testid="mfa-recovery-done">Jeg har gemt dem</button>}
      </div>
    </div>
  );
}

/** QR-kode + nøgle + første kode. `start()` giver {qr, secret, otpauth_uri};
 *  `confirm(code)` slår 2FA til og giver {recovery_codes, ...}. */
export function MfaSetup({ start, confirm, onConfirmed, intro }) {
  const [info, setInfo] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    let off = false;
    start().then(d => { if (!off) setInfo(d); }).catch(e => { if (!off) setErr(formatApiError(e)); });
    return () => { off = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true); setErr("");
    try { onConfirmed(await confirm(code.replace(/\s/g, ""))); }
    catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(false); }
  };
  if (!info) return <div className="py-8 flex justify-center">{err ? <p className="text-sm text-red-600">{err}</p> : <Loader2 className="w-6 h-6 animate-spin text-slate-400" />}</div>;
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="mfa-setup">
      {intro && <p className="text-sm text-slate-600 dark:text-slate-300">{intro}</p>}
      <ol className="text-sm text-slate-600 dark:text-slate-300 list-decimal pl-5 space-y-1">
        <li>Åbn en godkendelses-app på telefonen (fx Microsoft Authenticator eller Google Authenticator).</li>
        <li>Scan QR-koden - eller skriv nøglen ind.</li>
        <li>Skriv den 6-cifrede kode, appen viser.</li>
      </ol>
      <div className="flex flex-col items-center gap-2">
        {info.qr ? <img src={info.qr} alt="QR-kode til godkendelses-appen" className="w-48 h-48 bg-white rounded-xl p-1" data-testid="mfa-qr" />
          : <a href={info.otpauth_uri} className="text-sm font-semibold text-orange-600 underline">Åbn i godkendelses-appen</a>}
        <div className="text-center">
          <div className="text-[11px] uppercase tracking-wide text-slate-400">Nøgle</div>
          <div className="font-mono text-sm select-all" data-testid="mfa-secret">{info.secret}</div>
        </div>
      </div>
      <input className={`${inputCls} text-center tracking-[0.4em] font-mono`} value={code} onChange={e => setCode(e.target.value)} inputMode="numeric"
        autoComplete="one-time-code" placeholder="123456" maxLength={7} autoFocus data-testid="mfa-setup-code" />
      {err && <p className="text-sm text-red-600" data-testid="mfa-error">{err}</p>}
      <button type="submit" disabled={busy || code.replace(/\s/g, "").length < 6} className={`${btn} w-full bg-orange-500 text-white`} data-testid="mfa-setup-confirm">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Slå 2FA til
      </button>
    </form>
  );
}

function CodeAsk({ label, onSubmit, onCancel, testid }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <form className="space-y-2" onSubmit={async (e) => { e.preventDefault(); setBusy(true); try { await onSubmit(code.replace(/\s/g, "")); } finally { setBusy(false); } }}>
      <p className="text-sm text-slate-600 dark:text-slate-300">{label}</p>
      <input className={`${inputCls} text-center tracking-[0.4em] font-mono`} value={code} onChange={e => setCode(e.target.value)} inputMode="numeric"
        autoComplete="one-time-code" placeholder="123456" maxLength={7} autoFocus data-testid={testid} />
      <div className="flex gap-2">
        <button type="button" onClick={onCancel} className={`${btn} bg-slate-100 dark:bg-slate-700`}>Annuller</button>
        <button type="submit" disabled={busy || code.length < 6} className={`${btn} bg-orange-500 text-white flex-1`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "OK"}</button>
      </div>
    </form>
  );
}

/** Firmaets regel: skal administratorerne bruge 2FA? Kun firmaets administrator
 *  (logistik: med rettigheden "Team") kan ændre den. */
function CompanyMfaRule({ myMfa }) {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(`${API}/company/mfa-policy`).then(r => setP(r.data)).catch(() => {}); }, []);
  if (!p || !p.can_change) return null;
  const logistics = p.company_type === "logistics";
  const set = async (required) => {
    if (!required && !window.confirm(logistics
      ? "Slå kravet fra?\n\nI har løndata (timer og løn) i ContainerFlow - uden 2FA er det kun adgangskoden, der beskytter dem. Vi anbefaler at have kravet slået til."
      : "Slå kravet fra?\n\nAdministratorer kan så logge ind med kun adgangskoden. De, der allerede har 2FA, beholder den, til de selv slår den fra.")) return;
    setBusy(true);
    try { const { data } = await axios.put(`${API}/company/mfa-policy`, { required }); setP(data); toast.success(required ? "2FA er nu krævet for jeres administratorer" : "2FA er nu valgfri"); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <section className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-2" data-testid="mfa-company-rule">
      <div className="font-semibold flex items-center gap-2"><Building2 className="w-4 h-4" /> Firmaets regel</div>
      <label className="flex items-start gap-3 cursor-pointer">
        <input type="checkbox" className="mt-1 w-4 h-4 accent-orange-500" checked={!!p.required} disabled={busy || (!p.required && !myMfa)}
          onChange={e => set(e.target.checked)} data-testid="mfa-company-required" />
        <span className="text-sm">
          <b>Kræv 2FA for {logistics ? "administratorer og koordinatorer" : "kunde- og lokations-admins"}</b>
          <span className="block text-slate-500 dark:text-slate-400">
            {logistics ? "Anbefalet, fordi I har løndata i ContainerFlow." : "Valgfrit - I har ingen løndata i ContainerFlow, kun bestillinger og rapporter."}
            {" "}Chauffører og bestillere bruger aldrig 2FA.{p.required === p.default ? " (Standard)" : ""}
          </span>
          {!p.required && !myMfa && <span className="block text-amber-700 dark:text-amber-300">Slå 2FA til for dig selv først.</span>}
        </span>
      </label>
    </section>
  );
}

/** "Login og sikkerhed": 2FA og adgangskode for den indloggede bruger. */
export function AccountSecurityModal({ open, onClose }) {
  const [st, setSt] = useState(null);
  const [mode, setMode] = useState("");      // setup | codes | disable | newcodes | password
  const [codes, setCodes] = useState(null);
  const [pw, setPw] = useState({ old: "", next: "" });
  const load = () => axios.get(`${API}/auth/mfa/status`).then(r => setSt(r.data)).catch(e => toast.error(formatApiError(e)));
  useEffect(() => { if (open) { setMode(""); setCodes(null); load(); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!open) return null;
  const done = (d) => { if (d?.token) setAuthToken(d.token); setCodes(d.recovery_codes || null); setMode(d.recovery_codes ? "codes" : ""); load(); };
  const changePw = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/auth/change-password`, { old_password: pw.old, new_password: pw.next });
      if (data?.token) setAuthToken(data.token);
      toast.success("Adgangskoden er skiftet - du er logget ud alle andre steder");
      setPw({ old: "", next: "" }); setMode("");
    } catch (e2) { toast.error(formatApiError(e2)); }
  };
  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid="account-security">
      <div className="absolute inset-0 bg-slate-900/60" onClick={onClose} />
      <div className="relative w-full max-w-md max-h-[90vh] overflow-y-auto bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-6 space-y-4 text-slate-900 dark:text-white">
        <div className="flex items-start justify-between">
          <h2 className="font-bold text-lg flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-orange-500" /> Login og sikkerhed</h2>
          <button onClick={onClose} className="p-1 text-slate-400" aria-label="Luk"><X className="w-5 h-5" /></button>
        </div>
        {!st ? <div className="py-6 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : mode === "codes" && codes ? (
          <RecoveryCodes codes={codes} onDone={() => { setCodes(null); setMode(""); }} />
        ) : mode === "setup" ? (
          <MfaSetup start={async () => (await axios.post(`${API}/auth/mfa/setup`)).data}
            confirm={async (code) => (await axios.post(`${API}/auth/mfa/setup/confirm`, { code })).data} onConfirmed={done} />
        ) : mode === "newcodes" ? (
          <CodeAsk label="Skriv en kode fra appen for at lave nye gendannelseskoder (de gamle holder op med at virke)." testid="mfa-newcodes-code"
            onCancel={() => setMode("")}
            onSubmit={async (code) => { try { const { data } = await axios.post(`${API}/auth/mfa/recovery-codes`, { code }); setCodes(data.recovery_codes); setMode("codes"); load(); } catch (e) { toast.error(formatApiError(e)); } }} />
        ) : mode === "disable" ? (
          <CodeAsk label="Skriv en kode fra appen for at slå 2FA fra." testid="mfa-disable-code" onCancel={() => setMode("")}
            onSubmit={async (code) => { try { await axios.post(`${API}/auth/mfa/disable`, { code }); toast.success("2FA er slået fra - log ind igen"); window.location.assign("/"); } catch (e) { toast.error(formatApiError(e)); } }} />
        ) : mode === "password" ? (
          <form onSubmit={changePw} className="space-y-2">
            <input type="password" className={inputCls} placeholder="Nuværende adgangskode" value={pw.old} onChange={e => setPw({ ...pw, old: e.target.value })} autoComplete="current-password" />
            <input type="password" className={inputCls} placeholder="Ny adgangskode (mindst 10 tegn)" value={pw.next} onChange={e => setPw({ ...pw, next: e.target.value })} autoComplete="new-password" />
            <div className="flex gap-2">
              <button type="button" onClick={() => setMode("")} className={`${btn} bg-slate-100 dark:bg-slate-700`}>Annuller</button>
              <button type="submit" disabled={!pw.old || pw.next.length < 10} className={`${btn} bg-orange-500 text-white flex-1`}>Skift adgangskode</button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <section className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-2" data-testid="mfa-status">
              <div className="font-semibold flex items-center gap-2"><Smartphone className="w-4 h-4" /> To-trinsbekræftelse (2FA)</div>
              {st.enabled ? (
                <>
                  <p className="text-sm text-emerald-700 dark:text-emerald-300">Slået til{st.enabled_at ? ` siden ${new Date(st.enabled_at).toLocaleDateString("da-DK")}` : ""}. {st.recovery_left} gendannelseskoder tilbage.</p>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={() => setMode("newcodes")} className={`${btn} bg-slate-100 dark:bg-slate-700`}>Nye gendannelseskoder</button>
                    {st.devices > 0 && <button type="button" className={`${btn} bg-slate-100 dark:bg-slate-700`}
                      onClick={async () => { await axios.delete(`${API}/auth/mfa/devices`); toast.success("Glemt - koden skal bruges næste gang på alle enheder"); load(); }}>
                      Glem huskede enheder ({st.devices})</button>}
                    {!st.required && <button type="button" onClick={() => setMode("disable")} className={`${btn} text-red-600`}>Slå fra</button>}
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-slate-600 dark:text-slate-300">{st.required ? "Krævet i jeres firma." : "Valgfrit, men anbefalet - beskytter kontoen, selv hvis adgangskoden slipper ud."}</p>
                  <button type="button" onClick={() => setMode("setup")} className={`${btn} bg-orange-500 text-white`} data-testid="mfa-enable">Slå 2FA til</button>
                </>
              )}
            </section>
            <CompanyMfaRule myMfa={!!st.enabled} />
            <section className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-2">
              <div className="font-semibold flex items-center gap-2"><KeyRound className="w-4 h-4" /> Adgangskode</div>
              <p className="text-sm text-slate-600 dark:text-slate-300">Når du skifter, bliver du logget ud på alle andre enheder.</p>
              <button type="button" onClick={() => setMode("password")} className={`${btn} bg-slate-100 dark:bg-slate-700`}>Skift adgangskode</button>
            </section>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

/** Lille knap til sidehovedet (ved "Log ud"). */
export function AccountSecurityButton({ className = "", label = "Sikkerhed" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={className || "flex items-center gap-1.5 text-xs text-slate-300 hover:text-white"} title="Login og sikkerhed (2FA)" data-testid="account-security-btn">
        <ShieldCheck className="w-4 h-4" /> <span className="hidden sm:inline">{label}</span>
      </button>
      <AccountSecurityModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}
